import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  ArrowLeft, Send, MessageSquare, Loader2, Check, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const CustomerChatPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    initializeChat();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initializeChat = async () => {
    try {
      // Get existing conversations
      const response = await api.get('/conversations');
      const conversations = response.data.conversations || [];
      
      // Find conversation for this job or any active one
      let conv = jobId 
        ? conversations.find(c => c.job_id === jobId)
        : conversations[0];
      
      if (conv) {
        setConversation(conv);
        await fetchMessages(conv.id);
        await markAsRead(conv.id);
        connectWebSocket(conv.id);
      } else {
        // No conversation yet - show empty state
        setConversation(null);
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      toast.error('Failed to load chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      await api.post(`/conversations/${conversationId}/read`);
    } catch (error) {
      // Silent fail
    }
  };

  const connectWebSocket = (conversationId) => {
    const token = localStorage.getItem('movemate_token');
    if (!token) return;
    
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https', 'wss').replace('http', 'ws')}/ws/chat/${conversationId}?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
          markAsRead(conversationId);
        }
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket error:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      // If no conversation exists, start one first
      if (!conversation) {
        const startResponse = await api.post('/conversations/start', {
          job_id: jobId || undefined,
          subject: 'General Inquiry',
          message: newMessage.trim()
        });
        
        const convId = startResponse.data.id;
        
        // Refresh to get the conversation
        const convResponse = await api.get('/conversations');
        const conversations = convResponse.data.conversations || [];
        const newConv = conversations.find(c => c.id === convId);
        
        if (newConv) {
          setConversation(newConv);
          await fetchMessages(convId);
          connectWebSocket(convId);
        }
        
        setNewMessage('');
        toast.success('Message sent!');
        return;
      }
      
      // Send to existing conversation
      const response = await api.post(`/conversations/${conversation.id}/messages`, {
        content: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (msg) => msg.sender_id === user?.id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="customer-chat-page">
      {/* Header */}
      <header className="bg-white border-b p-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </Button>
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary">M</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold">Your Moving Company</p>
          <p className="text-xs text-gray-500">
            {conversation ? 'Online' : 'No conversation yet'}
          </p>
        </div>
      </header>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {!conversation ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MessageSquare size={64} className="mx-auto mb-4 opacity-30" />
              <p className="font-medium mb-2">Start a conversation</p>
              <p className="text-sm">Type a message below to contact your moving company</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MessageSquare size={64} className="mx-auto mb-4 opacity-30" />
              <p className="font-medium mb-2">Start the conversation</p>
              <p className="text-sm">Send a message to your moving team</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pb-4">
            {messages.map((msg, idx) => {
              const isOwn = isOwnMessage(msg);
              return (
                <div
                  key={msg.id || idx}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%]`}>
                    {!isOwn && (
                      <p className="text-xs text-gray-500 mb-1 ml-1">
                        {msg.sender_name || 'Company'}
                      </p>
                    )}
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        isOwn
                          ? 'bg-primary text-white rounded-br-md'
                          : 'bg-white border rounded-bl-md shadow-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                      isOwn ? 'justify-end pr-1' : 'justify-start pl-1'
                    }`}>
                      <span>{formatTime(msg.created_at)}</span>
                      {isOwn && (
                        msg.status === 'read' ? (
                          <CheckCheck size={14} className="text-blue-500" />
                        ) : (
                          <Check size={14} />
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Message Input - Always show */}
      <form onSubmit={handleSend} className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={conversation ? "Type a message..." : "Start a conversation..."}
            className="flex-1 rounded-full"
            disabled={sending}
            data-testid="customer-message-input"
          />
          <Button 
            type="submit" 
            size="icon"
            className="rounded-full"
            disabled={!newMessage.trim() || sending}
            data-testid="customer-send-btn"
          >
            {sending ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Send size={18} />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerChatPage;
